import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, orderBy, query, writeBatch } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { User } from '../../../types';
import { LinkedChildData } from '../services/ParentService';
import type { PublicLocation } from '../../partners/types';
import '../styles/ParentAccountSettingsModal.css';

type PickupPreference = { options: string[]; otherText?: string } | null;

const PICKUP_LABELS: Array<{ key: string; label: string }> = [
  { key: 'aftercare', label: 'Aftercare' },
  { key: 'guardian_pickup', label: 'Guardian Pick-up' },
  { key: 'bike_walk', label: 'Bike/Walk' },
  { key: 'other', label: 'Other' },
];

function nameParts(displayName?: string | null): { first: string; last: string } {
  if (!displayName) return { first: '', last: '' };
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

function normalizePhone(value: string): { formatted: string; digits: string } | null {
  const digits = value.replace(/[^0-9]/g, '').slice(0, 10);
  if (digits.length !== 10) return null;
  const formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return { formatted, digits };
}

function normalizePickupPreference(options: string[], otherText: string): PickupPreference {
  const cleanedOptions = Array.from(
    new Set(
      (options || [])
        .filter((v) => typeof v === 'string')
        .map((v) => v.trim())
        .filter(Boolean)
        .slice(0, 10)
        .map((v) => v.slice(0, 64))
    )
  );
  if (cleanedOptions.length === 0) return null;
  const trimmedOther = (otherText || '').trim().slice(0, 200);
  if (cleanedOptions.includes('other') && trimmedOther) {
    return { options: cleanedOptions, otherText: trimmedOther };
  }
  return { options: cleanedOptions };
}

function pickupEquals(a: any, b: any): boolean {
  const norm = (pp: any) => {
    if (!pp) return null;
    const opts: string[] = Array.isArray(pp)
      ? pp
      : Array.isArray(pp?.options)
        ? pp.options
        : [];
    const other = typeof pp?.otherText === 'string' ? pp.otherText.trim() : '';
    const unique = Array.from(new Set(opts.map((o) => String(o).trim()).filter(Boolean)));
    return {
      options: unique,
      otherText: other || undefined,
    };
  };
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

interface LocationOption {
  id: string;
  name: string;
  slug: string;
  partnerId: string;
}

interface ParentAccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  parent: User;
  linkedChildren: LinkedChildData[];
  onSaved: () => void;
}

const ParentAccountSettingsModal: React.FC<ParentAccountSettingsModalProps> = ({
  isOpen,
  onClose,
  parent,
  linkedChildren,
  onSaved,
}) => {
  const parts = useMemo(() => nameParts(parent.displayName), [parent.displayName]);
  const initialPhone = (Array.isArray((parent as any).phones) && (parent as any).phones.length > 0
    ? (parent as any).phones[0]
    : (parent as any).parentPhone) as string | undefined;

  const [firstName, setFirstName] = useState(parts.first);
  const [lastName, setLastName] = useState(parts.last);
  const [phone, setPhone] = useState(initialPhone || '');
  const [locationId, setLocationId] = useState<string>(((parent as any).locationId || '') as string);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [childPickupOptions, setChildPickupOptions] = useState<Record<string, string[]>>({});
  const [childPickupOtherText, setChildPickupOtherText] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load canonical public location projection for parent-safe selection.
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const locationsRef = collection(db, 'publicLocations');
        const q = query(locationsRef, orderBy('locationName'));
        const snap = await getDocs(q);
        const list: LocationOption[] = snap.docs.map(d => {
          const value = { id: d.id, ...(d.data() as any) } as PublicLocation;
          return {
            id: value.id,
            name: `${value.locationName} — ${value.partnerName}`,
            slug: value.locationSlug,
            partnerId: value.partnerId,
          };
        });
        setLocationOptions(list);
      } catch {
        setLocationOptions([]);
      }
    })();
  }, [isOpen]);

  // Reset draft when opening
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSaving(false);
    setFirstName(parts.first);
    setLastName(parts.last);
    setPhone(initialPhone || '');
    setLocationId(((parent as any).locationId || '') as string);

    const opts: Record<string, string[]> = {};
    const other: Record<string, string> = {};
    linkedChildren.forEach((c) => {
      const pp: any = (c.child as any).pickupPreference;
      const options: string[] = Array.isArray(pp)
        ? pp
        : Array.isArray(pp?.options)
          ? pp.options
          : [];
      opts[c.child.id] = options
        .filter((v) => typeof v === 'string')
        .map((v) => v.trim())
        .filter(Boolean);
      other[c.child.id] = typeof pp?.otherText === 'string' ? pp.otherText : '';
    });
    setChildPickupOptions(opts);
    setChildPickupOtherText(other);
  }, [isOpen, parts.first, parts.last, initialPhone, parent, linkedChildren]);

  if (!isOpen) return null;

  const toggleChildPickupOption = (childId: string, key: string) => {
    setChildPickupOptions((prev) => {
      const current = prev[childId] || [];
      const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
      // If "other" was unchecked, clear other text
      if (!next.includes('other')) {
        setChildPickupOtherText((o) => ({ ...o, [childId]: '' }));
      }
      return { ...prev, [childId]: next };
    });
  };

  const saveAll = async () => {
    setSaving(true);
    setError(null);
    try {
      const batch = writeBatch(db);

      // Parent updates
      const updates: any = {};
      const newDisplayName = `${firstName.trim()} ${lastName.trim()}`.trim();
      if ((parent.displayName || '').trim() !== newDisplayName) {
        updates.displayName = newDisplayName;
      }
      const selectedLocation = locationOptions.find(option => option.id === locationId);
      if (String((parent as any).locationId || '') !== locationId) {
        updates.locationId = selectedLocation?.id || null;
        updates.locationSlug = selectedLocation?.slug || null;
        updates.partnerId = selectedLocation?.partnerId || null;
      }

      const trimmedPhone = phone.trim();
      const currentRaw = String(initialPhone || '').trim();
      if (!trimmedPhone) {
        // Allow clearing
        if (currentRaw) {
          updates.phones = [];
          updates.parentPhone = null;
        }
      } else {
        const normalized = normalizePhone(trimmedPhone);
        if (!normalized) {
          throw new Error('Phone numbers must have exactly 10 digits, e.g. (312) 863-9096.');
        }
        const currentDigits = currentRaw.replace(/[^0-9]/g, '');
        if (currentDigits !== normalized.digits) {
          updates.phones = [normalized.formatted];
          updates.parentPhone = normalized.formatted;
        }
      }

      if (Object.keys(updates).length > 0) {
        batch.update(doc(db, 'users', parent.id), updates);
      }

      // Child pickupPreference updates
      linkedChildren.forEach((c) => {
        const childId = c.child.id;
        const draft = normalizePickupPreference(childPickupOptions[childId] || [], childPickupOtherText[childId] || '');
        const current = (c.child as any).pickupPreference;
        if (!pickupEquals(current, draft)) {
          batch.update(doc(db, 'users', childId), { pickupPreference: draft });
        }
      });

      await batch.commit();
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal parent-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Account Settings</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body parent-settings-body">
          {error && <div className="error-message">{error}</div>}

          <div className="settings-section">
            <h4>Your Info</h4>
            <div className="settings-grid">
              <div className="form-group">
                <label>Parent first name</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Parent last name</label>
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <div className="form-group full-width">
                <label>Parent phone number</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(312) 863-9096" />
              </div>
              <div className="form-group full-width">
                <label>Home program location</label>
                <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  <option value="">Select location</option>
                  {locationOptions.map((location) => (
                    <option key={location.id} value={location.id}>{location.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h4>Pickup procedure</h4>
            {linkedChildren.length === 0 ? (
              <p className="muted">Add a child first to set pickup procedure.</p>
            ) : (
              <div className="pickup-children">
                {linkedChildren.map((c) => {
                  const childId = c.child.id;
                  const childName = c.child.displayName || 'Student';
                  const options = childPickupOptions[childId] || [];
                  const otherText = childPickupOtherText[childId] || '';
                  return (
                    <div key={childId} className="pickup-child-card">
                      <div className="pickup-child-title">{childName}</div>
                      <div className="pickup-options">
                        {PICKUP_LABELS.map((opt) => (
                          <label key={opt.key} className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={options.includes(opt.key)}
                              onChange={() => toggleChildPickupOption(childId, opt.key)}
                              disabled={saving}
                            />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      {options.includes('other') && (
                        <div className="pickup-other">
                          <label className="pickup-other-label">Other (please specify)</label>
                          <input
                            type="text"
                            value={otherText}
                            onChange={(e) => setChildPickupOtherText((prev) => ({ ...prev, [childId]: e.target.value }))}
                            placeholder="e.g., carpool, extended day, etc."
                            disabled={saving}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="settings-actions">
            <button className="submit-button" disabled={saving} onClick={saveAll}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentAccountSettingsModal;



